import "./input.css";
import "./datepicker.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { inputFunc } from "../../lib/inputFunctions";

type TextInputProps = {
  state: { [key: string]: any };
  setState: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
  stateKey: string;
  label?: string;
  inlineLabel?: string;
  labelWidth?: number;
  placeholder?: string;
  required?: boolean;
};

export function DateInput({
  state,
  stateKey,
  setState,
  label,
  inlineLabel,
  labelWidth,
  placeholder,
  required,
}: TextInputProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const monthSting = [
    "Január",
    "Február",
    "Március",
    "Április",
    "Május",
    "Június",
    "Július",
    "Augusztus",
    "Szeptember",
    "Október",
    "November",
    "December",
  ];

  const [active, setActive] = useState(false);

  //current date
  const date = new Date();

  //Initial and setted date states
  const [year, setYear] = useState<number>(date.getFullYear());
  const [month, setMonth] = useState<number>(date.getMonth() + 1);
  const [selected, setSelected] = useState<{ [key: string]: null | number }>({ year: null, month: null, day: null });

  const [activeSelector, setActiveSelector] = useState(1);

  const daysOfMonth = new Date(year, month, 0).getDate();
  const firstDay = (new Date(year, month - 1).getDay() + 6) % 7;
  const lastDay = 7 - new Date(year, month - 1, daysOfMonth).getDay();
  const today = { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };

  useEffect(() => {
    const date = new Date(state[stateKey]);
    if (!state[stateKey]) {
      return;
    }
    setSelected({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() });
    setYear(date.getFullYear());
    setMonth(date.getMonth() + 1);
  }, []);

  //Functions
  const daysOfPrevMonth = () => {
    const Arr = [];
    for (let i = 0; i < firstDay; i++) {
      Arr.push(daysOfMonth - firstDay + i + 1);
    }
    return Arr;
  };

  const daysOfNextMonth = () => {
    const Arr = [];
    if (daysOfPrevMonth().length + daysOfMonth <= 5 * 7 - 1) {
      for (let i = 0; i < lastDay + 7; i++) {
        Arr.push(i + 1);
      }
    } else {
      for (let i = 0; i < lastDay; i++) {
        Arr.push(i + 1);
      }
    }
    return Arr;
  };

  const prevMonth = () => {
    if (activeSelector === 1) {
      const num = month - 1;
      if (num >= 1) {
        setMonth((prev) => prev - 1);
      } else {
        setYear((prev) => prev - 1);
        setMonth(12);
      }
    }

    if (activeSelector === 2) {
      setYear((prev) => prev - 1);
    }
  };

  const nextMonth = () => {
    if (activeSelector === 1) {
      const num = month + 1;
      if (num <= 12) {
        setMonth((prev) => prev + 1);
      } else {
        setYear((prev) => prev + 1);
        setMonth(1);
      }
    }
    if (activeSelector === 2) {
      setYear((prev) => prev + 1);
    }
  };

  const onSelect = (selected: number, correction: number) => {
    const dispMonth = month + correction >= 10 ? month + correction : `0${month + correction}`;
    const dispDay = selected >= 10 ? selected : `0${selected}`;
    const formatted = `${year}-${dispMonth}-${dispDay}`;
    setSelected({ year: year, month: month + correction, day: selected });
    inputFunc.changeObj(formatted, setState, stateKey);
  };

  const changeSelector = () => {
    if (activeSelector === 1) {
      setActiveSelector(2);
    }

    if (activeSelector === 2) {
      setActiveSelector(3);
    }

    if (activeSelector === 3) {
      setActiveSelector(1);
    }
  };

  const selectedClass = (i: number) => {
    let classN = "";
    if (today.year === year && today.month === month && today.day === i + 1) {
      classN = "today";
    }
    if (selected.year === year && selected.month === month && selected.day === i + 1) {
      classN = "selectedDay";
    }
    return classN;
  };

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActive(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="input-container date-picker">
      {label && (
        <div className="input-label">
          <label>{label}</label>
        </div>
      )}
      <div className="input-wrapper">
        {inlineLabel && (
          <div style={{ width: labelWidth }} className="input-inline-label">
            {inlineLabel}
          </div>
        )}
        <div className="inputfield-wrapper">
          <input
            id={stateKey}
            name={stateKey}
            value={state[stateKey]}
            className={`inputfield ${inlineLabel && "inlinelabeled"}`}
            type="text"
            placeholder={placeholder}
            required={required}
            readOnly
            onClick={() => setActive(active ? false : true)}
          />
        </div>
        {state[stateKey] && (
          <div
            className="input-delete"
            onClick={() => {
              setSelected({ year: null, month: null, day: null });
              inputFunc.changeObj("", setState, stateKey);
              setYear(today.year);
              setMonth(today.month);
              setActive(false);
            }}>
            {
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="18px"
                viewBox="0 -960 960 960"
                width="18px"
                fill="#e3e3e3">
                <path d="M480-424 284-228q-11 11-28 11t-28-11q-11-11-11-28t11-28l196-196-196-196q-11-11-11-28t11-28q11-11 28-11t28 11l196 196 196-196q11-11 28-11t28 11q11 11 11 28t-11 28L536-480l196 196q11 11 11 28t-11 28q-11 11-28 11t-28-11L480-424Z" />
              </svg>
            }
          </div>
        )}
        <div className="input-after" onClick={() => setActive(active ? false : true)}>
          {
            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px">
              <path d="M200-80q-33 0-56.5-23.5T120-160v-560q0-33 23.5-56.5T200-800h40v-80h80v80h320v-80h80v80h40q33 0 56.5 23.5T840-720v560q0 33-23.5 56.5T760-80H200Zm0-80h560v-400H200v400Zm0-480h560v-80H200v80Zm0 0v-80 80Zm280 240q-17 0-28.5-11.5T440-440q0-17 11.5-28.5T480-480q17 0 28.5 11.5T520-440q0 17-11.5 28.5T480-400Zm-160 0q-17 0-28.5-11.5T280-440q0-17 11.5-28.5T320-480q17 0 28.5 11.5T360-440q0 17-11.5 28.5T320-400Zm320 0q-17 0-28.5-11.5T600-440q0-17 11.5-28.5T640-480q17 0 28.5 11.5T680-440q0 17-11.5 28.5T640-400ZM480-240q-17 0-28.5-11.5T440-280q0-17 11.5-28.5T480-320q17 0 28.5 11.5T520-280q0 17-11.5 28.5T480-240Zm-160 0q-17 0-28.5-11.5T280-280q0-17 11.5-28.5T320-320q17 0 28.5 11.5T360-280q0 17-11.5 28.5T320-240Zm320 0q-17 0-28.5-11.5T600-280q0-17 11.5-28.5T640-320q17 0 28.5 11.5T680-280q0 17-11.5 28.5T640-240Z" />
            </svg>
          }
        </div>
      </div>

      <div className={`calendar-wrapper no-highlight ${active ? "open" : ""}`}>
        {activeSelector !== 3 && (
          <div className="calendar-header">
            <div className="calendar-back" onClick={prevMonth}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="18px"
                viewBox="0 -960 960 960"
                width="18px"
                fill="#e3e3e3">
                <path d="M798-281 550-447q-9-6-13.5-14.5T532-480q0-10 4.5-18.5T550-513l248-166q5-4 11-5t11-1q16 0 28 11t12 29v330q0 18-12 29t-28 11q-5 0-11-1t-11-5Zm-400 0L150-447q-9-6-13.5-14.5T132-480q0-10 4.5-18.5T150-513l248-166q5-4 11-5t11-1q16 0 28 11t12 29v330q0 18-12 29t-28 11q-5 0-11-1t-11-5Zm-18-199Zm400 0Zm-400 90v-180l-136 90 136 90Zm400 0v-180l-136 90 136 90Z" />
              </svg>
            </div>
            <div className="calendar-month" onClick={() => changeSelector()}>
              {activeSelector === 1 ? `${year} ${monthSting[month - 1]}` : `${year}`}
            </div>
            <div className="calendar-forward" onClick={nextMonth}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="18px"
                viewBox="0 -960 960 960"
                width="18px"
                fill="#e3e3e3">
                <path d="M100-315v-330q0-18 12-29t28-11q5 0 11 1t11 5l248 166q9 6 13.5 14.5T428-480q0 10-4.5 18.5T410-447L162-281q-5 4-11 5t-11 1q-16 0-28-11t-12-29Zm400 0v-330q0-18 12-29t28-11q5 0 11 1t11 5l248 166q9 6 13.5 14.5T828-480q0 10-4.5 18.5T810-447L562-281q-5 4-11 5t-11 1q-16 0-28-11t-12-29ZM180-480Zm400 0Zm-400 90 136-90-136-90v180Zm400 0 136-90-136-90v180Z" />
              </svg>
            </div>
          </div>
        )}
        <div className="calendar-body">
          {activeSelector === 1 && (
            <>
              <div className="calendar-week">
                <div className="weekDays">H</div>
                <div className="weekDays">K</div>
                <div className="weekDays">Sze</div>
                <div className="weekDays">Cs</div>
                <div className="weekDays">P</div>
                <div className="weekDays">Szo</div>
                <div className="weekDays">V</div>
              </div>

              <div className="calendar-days">
                {daysOfPrevMonth().map((i: number) => (
                  <div className="prevDay" key={i}>
                    {i}
                  </div>
                ))}
                {Array.from({ length: daysOfMonth }).map((_, i: number) => (
                  <div className={`day ${selectedClass(i)}`} key={i} onClick={() => onSelect(i + 1, 0)}>
                    {i + 1}
                  </div>
                ))}
                {daysOfNextMonth().map((i: number) => (
                  <div className="nextDay" key={i}>
                    {i}
                  </div>
                ))}
              </div>
            </>
          )}
          {activeSelector === 2 && (
            <div className="calendar-months">
              {monthSting.map((i, index) => (
                <div
                  className={`month`}
                  key={i}
                  onClick={() => {
                    setMonth(index + 1);
                    setActiveSelector(1);
                  }}>
                  {i.slice(0, 3) + "."}
                </div>
              ))}
            </div>
          )}
          {activeSelector === 3 && (
            <div className="calendar-years">
              {Array.from({ length: 20 })
                .map((_, i) => today.year - i - 1)
                .reverse()
                .map((e) => (
                  <div
                    key={e}
                    className={`year`}
                    onClick={() => {
                      setYear(e);
                      setActiveSelector(2);
                    }}>
                    {e}
                  </div>
                ))}

              <div
                className={`year currentYear`}
                onClick={() => {
                  setYear(today.year);
                  setActiveSelector(2);
                }}>
                {today.year}
              </div>
              {Array.from({ length: 20 }).map((_, i) => (
                <div
                  key={today.year + i + 1}
                  className={`year`}
                  onClick={() => {
                    setYear(today.year + i + 1);
                    setActiveSelector(2);
                  }}>
                  {today.year + i + 1}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
